<template>
  <div class="latest-video-player" :class="[variant, { container: variant === 'desktop' }]">
    <p class="model-name" :class="{ 'text-center': variant === 'mobile' }">
      {{ data.modelName }}
    </p>

    <!-- Local player (vue-plyr, signed MP4) -->
    <div class="player-wrapper">
      <div
        v-if="isActualPreview && showMsgpreview"
        class="overlay-preview d-flex justify-content-center"
        @click="playVideo"
      >
        {{ msgPreview }}
      </div>
      <ClientOnly>
        <vue-plyr ref="plyrRef" :options="plyrOptions">
          <video controls playsinline :poster="data.cover">
            <source :src="videoUrl" type="video/mp4" />
          </video>
        </vue-plyr>
      </ClientOnly>
    </div>
  </div>
</template>

<script setup lang="ts">
import VuePlyr from "vue-plyr";
import "vue-plyr/dist/vue-plyr.css";
import type { LatestVideoData } from "@/composables/useLatestVideo";

const props = defineProps<{
  data: LatestVideoData;
  contentType: "video" | "making-of" | "live";
  isPreview: boolean;
  variant: "desktop" | "mobile";
}>();

defineEmits(["showModalMessages"]);

const route = useRoute();

const videoUrl = computed(() =>
  props.contentType === "video" ? props.data.videoPath : props.data.livePath
);

const isActualPreview = computed(
  () => props.isPreview || props.data.name === "preview"
);

const msgPreview = computed(() =>
  route.params.language === "pt-br"
    ? "Clique aqui e veja uma prévia"
    : "Click here to see a preview"
);

const showMsgpreview = ref(true);
const plyrRef = ref<any>(null);

const plyrOptions = ref({
  controls: [
    "play-large",
    "restart",
    "rewind",
    "play",
    "fast-forward",
    "progress",
    "current-time",
    "duration",
    "mute",
    "volume",
    "captions",
    "settings",
    "fullscreen",
  ],
  fullscreen: {
    enabled: true,
    iosNative: true,
  },
  settings: ["quality", "speed"],
  debug: false,
  // força a proporção 16:9 (reserva a área correta mesmo antes de a fonte
  // reportar dimensões) — evita o player colapsar/não preencher a altura
  ratio: "16:9",
});

function playVideo() {
  if (plyrRef.value?.player) {
    plyrRef.value.player.play();
  }
}

watch(plyrRef, (value, old) => {
  if (old?.player) {
    old.player.off("play");
    old.player.off("pause");
    old.player.off("ended");
  }

  if (!value || !isActualPreview.value) return;

  value.player.on("play", () => {
    setTimeout(() => {
      showMsgpreview.value = false;
    }, 10000);
  });

  value.player.on("pause", () => {
    showMsgpreview.value = true;
  });

  value.player.on("ended", () => {
    showMsgpreview.value = true;
  });
});

onBeforeUnmount(() => {
  if (plyrRef.value?.player) {
    plyrRef.value.player.off("play");
    plyrRef.value.player.off("pause");
    plyrRef.value.player.off("ended");
  }
});
</script>

<style scoped lang="css">
.latest-video-player {
  padding: 16px 0;
  background-color: #f8f8f8;
}

.latest-video-player.desktop {
  padding: 16px 0;
}

.plyr video {
  width: 100%;
  aspect-ratio: 16 / 9;
  object-fit: contain;
}

.plyr:fullscreen video {
    max-height: 100vh !important;
    width: 100vw !important;
    object-fit: contain;
}

.latest-video-player.mobile {
  padding: 16px 0px 30px;
}

.model-name {
  color: #868686;
  font-weight: 600;
  font-size: 16px;
  margin-bottom: 8px;
  text-align: left;
}

.player-wrapper {
  position: relative;
}

.overlay-preview {
  font-family: "Bebas-Neue";
  font-size: 40px;
  font-weight: 400;
  position: absolute;
  z-index: 2;
  color: #ffffff;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: rgba(255, 255, 255, 0.2);
  width: 100%;
  height: 80px;
  line-height: 90px;
  cursor: pointer;
}

@media screen and (max-width: 992px) {
  .overlay-preview {
    font-size: 25px;
    height: 50px;
    line-height: 55px;
  }
  :deep(.plyr video) {
    aspect-ratio: 16 / 9;
  }
  .latest-video-player.mobile {
    padding: 5px 0px 30px;
  }

}
</style>
